import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { firstAllowedPath, switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { useMonopolyReport } from '@/features/monopoly/useMonopolyReport';
import { fmtClock, isMonopolyTab, type MonopolyTab } from '@/features/monopoly/utils';
import { Header } from '@/features/monopoly/components/Header';
import { OverviewTab } from '@/features/monopoly/components/OverviewTab';
import { CardsTab, ParticipantsTab, PrizesTab, VisitsTab } from '@/features/monopoly/components/DetailTabs';
import { CompareTab } from '@/features/monopoly/components/CompareTab';
import '@/features/monopoly/monopoly.css';

/*
 * Monopoly 2026 realtime dashboard — thin orchestrator: poll the report (with
 * fixture fallback until the pipeline lands), keep the active tab in the URL,
 * and render the design's five tabs.
 */

/** Active tab lives in `?tab=` so a bookmark or a shared link opens the right one. */
function useMonopolyTab(): [MonopolyTab, (t: MonopolyTab) => void] {
  const read = (): MonopolyTab => {
    const v = new URLSearchParams(window.location.search).get('tab');
    return isMonopolyTab(v) ? v : 'overview';
  };
  const [tab, setTabState] = useState<MonopolyTab>(read);
  useEffect(() => {
    const onPop = () => setTabState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const setTab = useCallback((next: MonopolyTab) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.pushState(null, '', url);
    setTabState(next);
    window.scrollTo({ top: 0 });
  }, []);
  return [tab, setTab];
}

export function MonopolyDashboardPage({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isPreview, lastRefreshedAt } = useMonopolyReport(config.reportId, config.apiBaseUrl);
  const [tab, setTab] = useMonopolyTab();

  // Same allow-list guard as the other dashboards: signed in, but this report
  // isn't in the token → bounce to the first one that is.
  const allowed = auth?.allowedReports ?? [];
  if (allowed.length > 0 && !allowed.includes(config.reportId)) {
    const target = firstAllowedPath(allowed);
    if (target && target !== config.path) return <Navigate to={target} replace />;
  }

  const switchOptions = reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="mono-root">
      <div className="mono-app">
        <Header
          campaign={data.campaign}
          updatedLabel={fmtClock(lastRefreshedAt)}
          isPreview={isPreview}
          tab={tab}
          onTab={setTab}
          reportId={config.reportId}
          baseUrl={config.apiBaseUrl}
          switchOptions={switchOptions}
          onSwitch={(p) => navigate(p)}
          onSignOut={onSignOut}
          isAdmin={auth?.role === 'admin'}
          onManageUsers={() => navigate('/admin/users')}
          onWinners={() => navigate(`${config.path}/winners`)}
        />
        <main className="mono-main">
          {tab === 'overview' && <OverviewTab data={data} onTab={setTab} />}
          {tab === 'participants' && <ParticipantsTab data={data} />}
          {tab === 'visits' && <VisitsTab data={data} />}
          {tab === 'cards' && <CardsTab data={data} />}
          {tab === 'prizes' && <PrizesTab data={data} />}
          {tab === 'compare' && <CompareTab data={data} />}
          <div className="mono-foot">
            {isPreview
              ? "Chiffres d'exemple pour la revue de design · le pipeline n'a pas encore publié"
              : 'Sources : MNGS · transactions Carrefour (Drive / LAD / LEX) · événements analytics · actualisé toutes les quelques minutes'}
          </div>
        </main>
      </div>
    </div>
  );
}
